import React, { createContext, useCallback, useContext, useState } from "react";
import { useNotification } from "../notification-context";
import { postAuthenticatedRequest } from "../../../utils/api/requests/request";
import { cleanObject } from "../../../utils/helper/format";
import { downloadFileType } from "../../../shared/constants/constants";
import { downloadFile } from "../../../utils/helper/util";

const TransactionContext = createContext();

export const TransactionProvider = ({ children }) => {
  const [transactions, setTransactions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [depositTransactions, setDepositTransactions] = useState(null);
  const [depositTransactionDetails, setDepositTransactionDetails] =
    useState(null);
  const [coreTransactionDetails, setCoreTransactionDetails] = useState(null);
  const [isCoreTransactionDetailsLoading, setIsCoreTransactionDetailsLoading] =
    useState(false);
  const [journalTransactionDetails, setJournalTransactionDetails] = useState(null);
  const [isJournalTransactionDetailsLoading, setIsJournalTransactionDetailsLoading] =
    useState(false);
  const [isDepositTransactionLoading, setIsDepositTransactionLoading] =
    useState(false);
  const [
    isDepositTransactionDetailsLoading,
    setIsDepositTransactionDetailsLoading,
  ] = useState(false);
  const [loanTransactions, setLoanTransactions] = useState(null);
  const [isLoanTransactionLoading, setIsLoanTransactionLoading] =
    useState(false);
  const { triggerNotification } = useNotification();
  const [filter, setFilter] = useState({
    requestStatus: "",
    requestType: "",
    excludeTransfers: true,
    searchText: "",
    startDate: "",
    endDate: "",
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const [activeModal, setActiveModal] = useState(null);
  const openModal = (modalName) => {
    setActiveModal(modalName);
  };
  const closeModal = () => {
    setActiveModal(null);
  };
  const getTransactions = useCallback(async () => {
    try {
      setIsLoading(true);

      // Filter out properties with empty values
      const cleanedFilter = cleanObject({ ...filter, ...pagination });
      const response = await postAuthenticatedRequest(
        "GetSelfServiceTransactionsQuery",
        {
          operation: "GET",
          ...cleanedFilter,
        }
      );

      const result = response?.outData;
      if (result?.isSuccessful) {
        setTransactions(result);
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error?.message || "Failed to fetch transactions",
      });
    } finally {
      setIsLoading(false);
    }
  }, [triggerNotification, filter, pagination]);

  // download transaction report
  const downloadTransactionReport = useCallback(async () => {
    try {
      setIsDownloadingReport(true);

      // Filter out properties with empty values
      const cleanedFilter = cleanObject({
        ...filter,
        ...pagination,
        isExport: true,
        fileExportName: "TransactionReport",
        exportFields: {
          moduleType: "Type",
          requestId: "Request ID",
          integrationProviderDesc: "Service Provider",
          serviceName: "Service",
          transactionStatusDescription: "Status",
          transactionDate: "Transaction Date",
          currencyCode: "Currency",
          transactionAmount: "Amount",
          sourceAccountNumber: "Source Account",
          serviceSubscriberReference: "Subscriber Ref.",
          transactionRemarks: "Narration",
        },
      });
      const response = await postAuthenticatedRequest(
        "GetSelfServiceTransactionsQuery",
        {
          operation: "GET",
          ...cleanedFilter,
        }
      );

      const result = response?.outData;
      if (result?.isSuccessful) {
        downloadFile(
          result?.data,
          "Transaction_Report",
          downloadFileType.EXCEL
        );

        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Transaction report downloaded",
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error?.message || "Failed to fetch transactions",
      });
    } finally {
      setIsDownloadingReport(false);
    }
  }, [triggerNotification, filter, pagination]);

  const downloadTransactioPtspnReport = useCallback(async () => {
    try {
      setIsDownloadingReport(true);

      // Filter out properties with empty values
      const cleanedFilter = cleanObject({
        ...filter,
        ...pagination,
        isExport: true,
        fileExportName: "VirtualAccountTransactionsReport",
        exportFields: {
          moduleType: "Type",
          requestId: "Request ID",
          integrationProviderDesc: "Service Provider",
          serviceName: "Service",
          transactionStatusDescription: "Status",
          transactionDate: "Transaction Date",
          currencyCode: "Currency",
          transactionAmount: "Amount",
          sourceAccountNumber: "Source Account",
          serviceSubscriberReference: "Subscriber Ref.",
          transactionRemarks: "Narration",
        },
      });
      const response = await postAuthenticatedRequest(
        "RetrieveVirtualAccountTransactionsListQuery",
        {
          operation: "GET",
          ...cleanedFilter,
        }
      );

      const result = response?.outData;
      if (result?.isSuccessful) {
        downloadFile(
          result?.data,
          "Transaction_Report",
          downloadFileType.EXCEL
        );

        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Transaction report downloaded",
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error?.message || "Failed to fetch transactions",
      });
    } finally {
      setIsDownloadingReport(false);
    }
  }, [triggerNotification, filter, pagination]);


  const getDepositTransactions = useCallback(
    async ({ filter, pagination }) => {
      try {
        setIsDepositTransactionLoading(true);

        // Filter out properties with empty values
        const cleanedFilter = cleanObject({ ...filter, ...pagination });
        const response = await postAuthenticatedRequest(
          "RetrieveDepositTransactionListQuery",
          {
            ...cleanedFilter,
          },
          true
        );

        const result = response?.outData;
        if (result?.isSuccessful) {
          setDepositTransactions(result);
        } else {
          triggerNotification({
            type: "error",
            message: "Error",
            description: result?.message,
          });
        }
      } catch (error) {
        triggerNotification({
          type: "error",
          message: "Error",
          description: error?.message || "Failed to fetch deposit transactions",
        });
      } finally {
        setIsDepositTransactionLoading(false);
      }
    },
    [triggerNotification]
  );

  const getDepositTransactionById = useCallback(
    async (transactionId) => {
      try {
        setIsDepositTransactionDetailsLoading(true);
        const response = await postAuthenticatedRequest(
          "RetrieveDepositTransactionListQuery",
          {
            id: transactionId,
          },
          true
        );
        const result = response?.outData;
        if (result?.isSuccessful) {
          setDepositTransactionDetails(result?.data?.items?.[0]);
        } else {
          triggerNotification({
            type: "error",
            message: "Error",
            description: result?.message,
          });
        }
      } catch (error) {
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            error?.message || "Failed to fetch deposit transaction details",
        });
      } finally {
        setIsDepositTransactionDetailsLoading(false);
      }
    },
    [triggerNotification]
  );

  const getJournalTransactionById = useCallback(
    async (transactionId) => {
      try {
        setIsJournalTransactionDetailsLoading(true);
        const response = await postAuthenticatedRequest(
          "RetrieveJournalTransactionByIdQuery",
          {
            journalTransactionId: transactionId,
          },
          true
        );
        const result = response?.outData;
        console.log("result??? ", result?.data);
        if (result?.isSuccessful) {
          setJournalTransactionDetails(result?.data);
        } else {
          triggerNotification({
            type: "error",
            message: "Error",
            description: result?.message,
          });
        }
      } catch (error) {
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            error?.message || "Failed to fetch core transaction details",
        });
      } finally {
        setIsJournalTransactionDetailsLoading(false);
      }
    },
    [triggerNotification]
  );

  const downloadJournalTransactionReceipt = async (transactionId, fileName) => {
    try {
      setIsDownloadingReceipt(true);
      const response = await postAuthenticatedRequest(
        "DownloadCoreTransactionReceiptCommand",
        {
          transactionId,
        }
      );
      const result = response?.outData;
      if (result?.isSuccessful) {
        if (result?.data?.base64Receipt) {
          // Clean the base64 string to remove any data URL prefix (e.g., "data:application/pdf;base64,")
          const cleanBase64 = result.data.base64Receipt.replace(
            /^data:.*,/,
            ""
          );

          // Ensure the filename has a .pdf extension
          const fileNameWithExtension = fileName.endsWith(".pdf")
            ? fileName
            : `${fileName}.pdf`;

          downloadFile(
            cleanBase64,
            fileNameWithExtension,
            downloadFileType.PDF
          );
        }
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Core transaction receipt downloaded",
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description:
          error?.message || "Failed to download core transaction receipt",
      });
    } finally {
      setIsDownloadingReceipt(false);
    }
  };
  const getCoreTransactionById = useCallback(
    async (transactionId) => {
      try {
        setIsCoreTransactionDetailsLoading(true);
        const response = await postAuthenticatedRequest(
          "RetrieveCoreTransactionQuery",
          {
            transactionId,
          },
          true
        );
        const result = response?.outData;
        if (result?.isSuccessful) {
          setCoreTransactionDetails(result?.data);
        } else {
          triggerNotification({
            type: "error",
            message: "Error",
            description: result?.message,
          });
        }
      } catch (error) {
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            error?.message || "Failed to fetch core transaction details",
        });
      } finally {
        setIsCoreTransactionDetailsLoading(false);
      }
    },
    [triggerNotification]
  );

  const downloadCoreTransactionReceipt = async (transactionId, fileName) => {
    try {
      setIsDownloadingReceipt(true);
      const response = await postAuthenticatedRequest(
        "DownloadCoreTransactionReceiptCommand",
        {
          transactionId,
        }
      );
      const result = response?.outData;
      if (result?.isSuccessful) {
        if (result?.data?.base64Receipt) {
          // Clean the base64 string to remove any data URL prefix (e.g., "data:application/pdf;base64,")
          const cleanBase64 = result.data.base64Receipt.replace(
            /^data:.*,/,
            ""
          );

          // Ensure the filename has a .pdf extension
          const fileNameWithExtension = fileName.endsWith(".pdf")
            ? fileName
            : `${fileName}.pdf`;

          downloadFile(
            cleanBase64,
            fileNameWithExtension,
            downloadFileType.PDF
          );
        }
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Core transaction receipt downloaded",
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description:
          error?.message || "Failed to download core transaction receipt",
      });
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const getLoanTransactions = useCallback(
    async ({ filter, pagination }) => {
      try {
        setIsLoanTransactionLoading(true);
        const cleanedFilter = cleanObject({ ...filter, ...pagination });
        const response = await postAuthenticatedRequest(
          "RetrieveLoanTransactionListQuery",
          {
            ...cleanedFilter,
          },
          true
        );
        const result = response?.outData;
        if (result?.isSuccessful) {
          setLoanTransactions(result);
        } else {
          triggerNotification({
            type: "error",
            message: "Error",
            description: result?.message,
          });
        }
      } catch (error) {
        triggerNotification({
          type: "error",
          message: "Error",
          description: error?.message || "Failed to fetch loan transactions",
        });
      } finally {
        setIsLoanTransactionLoading(false);
      }
    },
    [triggerNotification]
  );

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        isLoading,
        isDownloadingReport,
        getTransactions,
        filter,
        setFilter,
        pagination,
        setPagination,
        downloadTransactionReport,
        downloadTransactioPtspnReport,
        getDepositTransactions,
        depositTransactions,
        isDepositTransactionLoading,
        getLoanTransactions,
        loanTransactions,
        isLoanTransactionLoading,
        getDepositTransactionById,
        journalTransactionDetails,
        isJournalTransactionDetailsLoading,
        getJournalTransactionById,
        downloadJournalTransactionReceipt,
        coreTransactionDetails,
        getCoreTransactionById,
        depositTransactionDetails,
        isDepositTransactionDetailsLoading,
        isCoreTransactionDetailsLoading,
        downloadCoreTransactionReceipt,
        isDownloadingReceipt,
        activeModal,
        openModal,
        closeModal,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransaction = () => {
  return useContext(TransactionContext);
};
